import { useColorMode } from './brand.js';
import { PortalCompanyPage } from './portal-company-page.js';
import { refreshSession, sessionQuery } from '../lib/session.js';
import { civilDateOf, formatDateTime } from '../lib/time-zone.js';
import {
  AvailabilityCalendar,
  Button,
  Card,
  CardHeader,
  Combobox,
  Input,
  Switch,
  useSnackbar,
} from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { CalendarDays, Clock3, MapPin, Search, ShieldCheck, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { accountApi } from '../lib/account-api.js';
import { contextApi, getApiError } from '../lib/api.js';
import { portalApi, type PortalCompany } from '../lib/portal-api.js';
import { portalServiceTerms } from '../lib/portal-service-terms.js';
import { ErrorCard, LoadingCard } from './data-view.js';
import { PortalFooter, PortalMasthead, SignInButtons } from './portal-masthead.js';
import './public-portal.css';

export function PublicPortal({ companyKey }: { companyKey?: string }) {
  return (
    <div className="public-portal min-h-screen bg-surface text-content">
      <PortalMasthead help />
      <main className="px-5 pb-20 pt-8 sm:px-8 sm:pt-12">
        {companyKey ? <PublicCompany companyKey={companyKey} /> : <PublicDirectory />}
        <PortalHelp />
      </main>
      <PortalFooter />
    </div>
  );
}
function PublicDirectory() {
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const result = useQuery({
    queryKey: ['public', 'portal', 'companies', search, offset],
    queryFn: () => portalApi.companies({ search, offset, limit: 12 }),
    retry: false,
  });
  return (
    <>
      <div className="portal-hero relative isolate overflow-hidden rounded-[2rem] px-6 py-10 sm:px-12 sm:py-16">
        <span className="portal-orbit" aria-hidden="true" />
        <div className="relative max-w-2xl">
          <Sparkles className="mb-5 size-7 text-brand-500" />
          <h1 className="portal-headline">
            Um horário
            <br />
            para você.
          </h1>
          <p className="mt-5 max-w-lg text-lg leading-7 text-content-secondary">
            Conheça empresas, encontre seu serviço e escolha o melhor momento para cuidar de você.
          </p>
          <div className="mt-8 max-w-xl">
            <Input
              label="Buscar empresa, cidade ou serviço"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setOffset(0);
              }}
              placeholder="O que você procura?"
            />
          </div>
        </div>
      </div>
      <div className="my-8 flex items-center gap-3">
        <Search className="size-5 text-brand-500" />
        <h2 className="text-xl font-semibold">Encontre uma empresa</h2>
        <span className="text-sm text-content-muted">
          {result.data?.total ?? 0} {(result.data?.total ?? 0) === 1 ? 'empresa' : 'empresas'}
        </span>
      </div>
      {result.isPending ? (
        <LoadingCard label="Buscando empresas" />
      ) : result.isError ? (
        <ErrorCard message="Não foi possível consultar as empresas. Tente novamente." />
      ) : result.data.companies.length === 0 ? (
        <Card>
          <CardHeader title="Nenhuma empresa encontrada" icon={<Search className="size-4" />} />
          <p className="text-sm text-content-muted">Tente outro nome, cidade ou serviço.</p>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {result.data.companies.map((company) => (
            <Link
              key={company.key}
              to="/c/$companyKey"
              params={{ companyKey: company.key }}
              className="portal-company-card overflow-hidden rounded-2xl border border-surface-border bg-surface-card"
            >
              <div className="portal-company-cover flex h-32 items-end p-6">
                <span className="text-4xl font-semibold text-brand-600">
                  {company.displayName.slice(0, 1)}
                </span>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold">{company.displayName}</h3>
                <p className="mt-3 flex items-center gap-2 text-sm text-content-muted">
                  <MapPin className="size-4" />
                  {company.units[0]?.city ?? 'Consulte a localização'}
                </p>
                <p className="mt-3 line-clamp-2 text-sm text-content-secondary">
                  {company.description || 'Conheça os serviços e horários disponíveis.'}
                </p>
                <span className="mt-6 inline-block font-semibold text-brand-600">
                  Ver serviços e horários
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
      {result.data && result.data.total > 12 ? (
        <div className="mt-8 flex items-center justify-center gap-4">
          <Button
            variant="outline"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - 12))}
          >
            Anterior
          </Button>
          <span className="text-sm">
            {offset + 1}–{Math.min(offset + 12, result.data.total)} de {result.data.total}
          </span>
          <Button
            variant="outline"
            disabled={offset + 12 >= result.data.total}
            onClick={() => setOffset(offset + 12)}
          >
            Próxima
          </Button>
        </div>
      ) : null}
    </>
  );
}
function PublicCompany({ companyKey }: { companyKey: string }) {
  const company = useQuery({
    queryKey: ['public', 'portal', 'company', companyKey],
    queryFn: () => portalApi.company(companyKey),
    retry: false,
  });
  if (company.isPending) return <LoadingCard label="Carregando serviços da empresa" />;
  if (company.isError) return <ErrorCard message="Esta empresa não está disponível no portal." />;
  return <CompanyBooking key={companyKey} company={company.data} />;
}
/// O que estava escolhido antes de entrar ou criar a conta: a pessoa volta e encontra o mesmo
/// pedido montado (ADR 0067). Fica só nesta aba do navegador e some depois de lido.
interface PendingBooking {
  unitId: string;
  serviceId: string;
  month: string;
  date: string | null;
  professionalId: string;
  selected: string | null;
}
function pendingKey(companyKey: string) {
  return `portal-booking:${companyKey}`;
}
function takePendingBooking(companyKey: string): PendingBooking | null {
  try {
    const raw = sessionStorage.getItem(pendingKey(companyKey));
    sessionStorage.removeItem(pendingKey(companyKey));
    return raw ? (JSON.parse(raw) as PendingBooking) : null;
  } catch {
    return null;
  }
}
function keepPendingBooking(companyKey: string, booking: PendingBooking) {
  try {
    sessionStorage.setItem(pendingKey(companyKey), JSON.stringify(booking));
  } catch {
    // Sem armazenamento da aba, a pessoa só escolhe de novo depois de entrar.
  }
}
function CompanyBooking({ company }: { company: PortalCompany }) {
  const mode = useColorMode();
  const session = useQuery(sessionQuery);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [pending] = useState(() => takePendingBooking(company.key));
  const [bookingOpen, setBookingOpen] = useState(!company.page || Boolean(pending));
  const person = session.data;
  /// Toda pessoa com conta pede como cliente, mesmo dentro da área de uma empresa (ADR 0067). Só a
  /// atuação administrativa do root não pede em nome de ninguém.
  const canRequest = Boolean(person) && person?.context?.kind !== 'PLATFORM_ACTING_ON_TENANT';
  const account = useQuery({
    queryKey: ['customer', 'account'],
    queryFn: accountApi.get,
    enabled: canRequest,
    retry: false,
  });
  const linked = useQuery({
    queryKey: ['customer', 'portal', 'linked', company.key],
    queryFn: () => portalApi.linked(company.key),
    enabled: canRequest,
    retry: false,
  });
  const [switchingToCustomer, setSwitchingToCustomer] = useState(false);
  const [contextError, setContextError] = useState<string>();
  async function switchToCustomer() {
    setContextError(undefined);
    setSwitchingToCustomer(true);
    try {
      await contextApi.switch({ kind: 'CUSTOMER' });
      const updated = await refreshSession(queryClient);
      if (updated?.context?.kind !== 'CUSTOMER') {
        throw new Error('Não foi possível confirmar a troca para cliente. Tente novamente.');
      }
      snackbar.success(
        'Você está como cliente. Confira o horário e aceite a política para solicitar.',
      );
    } catch (cause) {
      setContextError(getApiError(cause));
    } finally {
      setSwitchingToCustomer(false);
    }
  }
  const [acceptingLegal, setAcceptingLegal] = useState(false);
  const policy = useQuery({
    queryKey: ['public', 'portal', 'policy'],
    queryFn: portalApi.policy,
    retry: false,
  });
  const [accepted, setAccepted] = useState(false);
  const [requestKey, setRequestKey] = useState(() => crypto.randomUUID());
  const [requestError, setRequestError] = useState<string>();
  const [sending, setSending] = useState(false);

  const [unitId, setUnitId] = useState(
    pending?.unitId ?? (company.units.length === 1 ? company.units[0]!.id : ''),
  );
  const [serviceId, setServiceId] = useState(pending?.serviceId ?? '');
  const [date, setDate] = useState<string | null>(pending?.date ?? null);
  const [prepaidId, setPrepaidId] = useState('');
  const [packageSearch, setPackageSearch] = useState('');
  const packages = useQuery({
    queryKey: [
      'customer',
      'portal',
      'packages',
      company.key,
      unitId,
      serviceId,
      date,
      packageSearch,
    ],
    queryFn: () =>
      portalApi.prepaidBalances(company.key, {
        businessUnitId: unitId,
        serviceId,
        date: date ?? undefined,
        search: packageSearch,
        limit: 25,
        offset: 0,
      }),
    enabled: canRequest && linked.data === true && !!unitId && !!serviceId,
    retry: false,
  });
  const selectedPackage = packages.data?.balances.find((item) => item.id === prepaidId);
  // Sem busca e sem escolha, lista vazia quer dizer que a pessoa não tem sessão para este serviço.
  const noPackages =
    packages.isSuccess && !packageSearch && !prepaidId && packages.data.balances.length === 0;

  const [professionalId, setProfessionalId] = useState(pending?.professionalId ?? '');
  const [selected, setSelected] = useState<string | null>(pending?.selected ?? null);
  const unit = company.units.find((item) => item.id === unitId);
  const service = company.services.find((item) => item.id === serviceId);
  const terms = service ? portalServiceTerms(service, unitId) : undefined;
  const today = civilDateOf(new Date(), unit?.timeZone ?? 'UTC');
  const [month, setMonth] = useState(pending?.month ?? today.slice(0, 7));
  const maxDate = new Date(Date.parse(`${today}T12:00:00Z`) + 90 * 86400000)
    .toISOString()
    .slice(0, 10);
  const [findingDay, setFindingDay] = useState(false);
  const monthAvailability = useQuery({
    queryKey: [
      'public',
      'portal',
      'availability-month',
      company.key,
      unitId,
      serviceId,
      month,
      professionalId,
      prepaidId,
    ],
    enabled: Boolean(unit && service),
    queryFn: () =>
      portalApi.monthAvailability(company.key, {
        businessUnitId: unitId,
        serviceId,
        month,
        ...(prepaidId ? { balanceId: prepaidId } : {}),
        professionalId: professionalId || undefined,
      }),
    retry: false,
  });
  const depositUnavailable = monthAvailability.data?.unavailabilityReason === 'DEPOSIT_UNAVAILABLE';
  async function nextAvailableDay() {
    setFindingDay(true);
    setRequestError(undefined);
    try {
      let cursor = month;
      while (cursor <= maxDate.slice(0, 7)) {
        const result =
          cursor === month && monthAvailability.data
            ? monthAvailability.data
            : await portalApi.monthAvailability(company.key, {
                businessUnitId: unitId,
                serviceId,
                month: cursor,
                ...(prepaidId ? { balanceId: prepaidId } : {}),
                professionalId: professionalId || undefined,
              });
        const next = result.days.find(
          (day) => day.date > (date ?? today) && day.date <= maxDate && day.slots.length > 0,
        );
        if (next) {
          setMonth(cursor);
          setDate(next.date);
          setSelected(null);
          setAccepted(false);
          setRequestKey(crypto.randomUUID());
          return;
        }
        const [year, number] = cursor.split('-').map(Number) as [number, number];
        cursor = new Date(Date.UTC(year, number, 1)).toISOString().slice(0, 7);
      }
      setRequestError(
        'Não há outro dia com horários nos próximos 90 dias. Entre em contato com a unidade.',
      );
    } catch (cause) {
      setRequestError(getApiError(cause));
    } finally {
      setFindingDay(false);
    }
  }
  const availability = {
    ...monthAvailability,
    data:
      monthAvailability.data && date
        ? {
            date,
            slots: monthAvailability.data.days.find((day) => day.date === date)?.slots ?? [],
            professionals: monthAvailability.data.professionals,
          }
        : undefined,
  };
  function clear() {
    setPrepaidId('');
    setPackageSearch('');
    setAccepted(false);
    setRequestKey(crypto.randomUUID());
    setDate(null);
    setSelected(null);
    setProfessionalId('');
  }
  const slot = availability.data?.slots.find((item) => slotKey(item) === selected);
  /// O horário escolhido traz o preço e a duração de quem atende nele (ADR 0074, decisão 12).
  const shown = terms
    ? {
        durationMinutes: slot?.durationMinutes ?? terms.durationMinutes,
        priceCents: slot?.priceCents ?? terms.priceCents,
      }
    : undefined;
  const range = service?.units?.find((item) => item.businessUnitId === unitId);
  const pricesVary =
    new Set(availability.data?.slots.map((item) => item.priceCents ?? null) ?? []).size > 1;
  async function requestSlot() {
    setRequestError(undefined);
    if (!slot || !date || !policy.data || !accepted) {
      setRequestError('Escolha o horário e aceite a política antes de solicitar.');
      return;
    }
    setSending(true);
    try {
      await portalApi.request(company.key, {
        businessUnitId: unitId,
        serviceId,
        date,
        startTime: slot.startTime,
        endTime: slot.endTime,
        professionalId: professionalId || undefined,
        requestKey,
        policyVersion: policy.data.version,
        ...(prepaidId ? { prepaidBalanceId: prepaidId } : {}),
      });
      await queryClient.invalidateQueries({ queryKey: ['customer'] });
      snackbar.success(`Pedido enviado a ${company.displayName}. A confirmação chega por e-mail.`);
      await navigate({ to: '/account' });
    } catch (cause) {
      setRequestError(getApiError(cause));
    } finally {
      setSending(false);
    }
  }
  function keepChoice() {
    keepPendingBooking(company.key, { unitId, serviceId, month, date, professionalId, selected });
  }
  async function acceptLegal() {
    setAcceptingLegal(true);
    try {
      queryClient.setQueryData(['customer', 'account'], await accountApi.acceptLegal());
    } catch (cause) {
      setRequestError(getApiError(cause));
    } finally {
      setAcceptingLegal(false);
    }
  }
  if (company.page && !bookingOpen)
    return (
      <>
        <Link to="/" className="mb-5 inline-block text-sm text-content-muted">
          Voltar às empresas
        </Link>
        <PortalCompanyPage
          company={company}
          onBook={(unitId, serviceId) => {
            setUnitId(unitId);
            setServiceId(serviceId);
            clear();
            setBookingOpen(true);
          }}
        />
      </>
    );
  return (
    <section
      className="portal-booking hue-palette-scope text-content"
      aria-label="Agendar atendimento"
      data-palette={company.page?.palette}
      data-mode={mode}
    >
      <nav className="flex flex-wrap items-center gap-x-5 gap-y-3" aria-label="Voltar no portal">
        {company.page ? (
          <Button variant="outline" onClick={() => setBookingOpen(false)}>
            Voltar à página da empresa
          </Button>
        ) : null}
        <Link to="/" className="text-sm text-content-muted">
          Voltar às empresas
        </Link>
      </nav>
      <div className="portal-hero relative mt-6 overflow-hidden rounded-[2rem] px-6 py-10 sm:px-10">
        <span className="portal-orbit" aria-hidden="true" />
        <h1 className="relative text-4xl font-semibold tracking-tight sm:text-5xl">
          {company.displayName}
        </h1>
        <p className="relative mt-4 max-w-2xl text-content-secondary">{company.description}</p>
      </div>
      <div className="mt-8 grid items-start gap-8 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
        <div className="min-w-0 space-y-6">
          <Card>
            <CardHeader title="Escolha seu atendimento" icon={<Sparkles className="size-4" />} />
            <div className="grid gap-5 sm:grid-cols-2">
              <Combobox
                label="Unidade"
                placeholder="Escolha a unidade"
                selectedLabel={unit?.displayName ?? ''}
                onSelect={(choice) => {
                  if (choice.kind !== 'option') return;
                  const value = choice.value;
                  setUnitId(value);
                  clear();
                }}
                options={company.units.map((item) => ({ value: item.id, label: item.displayName }))}
              />
              <Combobox
                label="Serviço"
                placeholder="Escolha o serviço"
                selectedLabel={service?.displayName ?? ''}
                onSelect={(choice) => {
                  if (choice.kind !== 'option') return;
                  const value = choice.value;
                  setServiceId(value);
                  clear();
                }}
                options={company.services.map((item) => ({
                  value: item.id,
                  label: `${item.displayName} · ${portalServiceTerms(item, unitId).durationMinutes} min`,
                }))}
              />
            </div>
            {canRequest && linked.data === true && unit && service ? (
              <div className="mt-5">
                <Combobox
                  label="Usar meu pacote já comprado (opcional)"
                  placeholder="Buscar meus pacotes deste serviço"
                  selectedLabel={
                    selectedPackage
                      ? `${selectedPackage.packageName} · venda ${selectedPackage.saleNumber}`
                      : ''
                  }
                  loading={packages.isFetching}
                  disabled={noPackages}
                  onQueryChange={setPackageSearch}
                  options={(packages.data?.balances ?? []).map((item) => ({
                    value: item.id,
                    label: `${item.packageName} · venda ${item.saleNumber}`,
                    description: `${item.available} sessões livres${item.expiresOn ? ` · validade ${item.expiresOn.split('-').reverse().join('/')}` : ''}`,
                  }))}
                  onSelect={(choice) => {
                    if (choice.kind !== 'option') return;
                    setPrepaidId(choice.value);
                    setSelected(null);
                    setAccepted(false);
                    setRequestKey(crypto.randomUUID());
                  }}
                  onClear={() => {
                    setPrepaidId('');
                    setSelected(null);
                    setAccepted(false);
                    setRequestKey(crypto.randomUUID());
                  }}
                  emptyLabel="Nenhuma sessão de pacote disponível para este serviço, unidade e data"
                  error={
                    packages.error
                      ? getApiError(packages.error)
                      : prepaidId && !selectedPackage && !packages.isFetching
                        ? 'O pacote escolhido não está disponível nesta data. Escolha outro pacote ou altere a data.'
                        : undefined
                  }
                  hint={
                    noPackages
                      ? 'Você não tem sessão de pacote disponível para este serviço, unidade e data.'
                      : 'São exibidos somente os seus pacotes. A solicitação depende da confirmação da unidade; usar uma sessão não gera outra compra.'
                  }
                  status={
                    packages.data && packages.data.total > 25
                      ? 'Digite o nome para refinar os resultados'
                      : undefined
                  }
                />
              </div>
            ) : null}
            {unit ? (
              <div className="mt-5 space-y-3 text-sm text-content-muted">
                <p>
                  {[unit.street, unit.streetNumber, unit.district, unit.city, unit.state]
                    .filter(Boolean)
                    .join(', ')}
                  {unit.phone ? ` · ${unit.phone}` : ''}
                </p>
                {unit.email ? (
                  <a className="block underline underline-offset-2" href={`mailto:${unit.email}`}>
                    {unit.email}
                  </a>
                ) : null}
                {unit.openingHours?.length ? (
                  <div>
                    <p className="font-semibold text-content">Horário de funcionamento</p>
                    <ul className="mt-2 grid gap-1 sm:grid-cols-2">
                      {unit.openingHours.map((window) => {
                        const clock = (minute: number) =>
                          `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;
                        return (
                          <li key={`${window.weekday}-${window.startMinute}`}>
                            {
                              [
                                'Domingo',
                                'Segunda',
                                'Terça',
                                'Quarta',
                                'Quinta',
                                'Sexta',
                                'Sábado',
                              ][window.weekday]
                            }
                            : {clock(window.startMinute)}–{clock(window.endMinute)}
                          </li>
                        );
                      })}
                    </ul>
                    <p className="mt-2 text-xs">
                      As vagas dependem do serviço e da agenda de cada profissional. Fuso:{' '}
                      {unit.timeZone}.
                    </p>
                  </div>
                ) : (
                  <p>Consulte os horários disponíveis no calendário ou fale com a unidade.</p>
                )}
              </div>
            ) : null}
          </Card>
          {unit && service ? (
            <Card>
              <CardHeader
                title="Escolha dia e horário"
                icon={<CalendarDays className="size-4" />}
              />
              <p className="mb-5 text-sm text-content-muted">
                {unit.allowProfessionalChoice
                  ? 'Escolha um profissional ou veja qualquer profissional disponível.'
                  : 'O profissional será definido pela equipe.'}{' '}
                Horário de {unit.timeZone}.
              </p>
              {unit.allowProfessionalChoice ? (
                <div className="mb-5">
                  <Combobox
                    label="Profissional"
                    placeholder="Qualquer profissional disponível"
                    selectedLabel={
                      monthAvailability.data?.professionals.find(
                        (item) => item.id === professionalId,
                      )?.displayName ?? 'Qualquer profissional disponível'
                    }
                    onSelect={(choice) => {
                      if (choice.kind !== 'option') return;
                      const value = choice.value;
                      setProfessionalId(value);
                      setSelected(null);
                      setAccepted(false);
                      setRequestKey(crypto.randomUUID());
                    }}
                    options={[
                      { value: '', label: 'Qualquer profissional disponível' },
                      ...(monthAvailability.data?.professionals ?? []).map((item) => ({
                        value: item.id,
                        label: item.displayName,
                      })),
                    ]}
                  />
                </div>
              ) : null}
              {depositUnavailable ? (
                <p
                  role="status"
                  className="mb-4 rounded-xl border border-surface-border bg-surface-elevated p-4 text-sm"
                >
                  Este serviço exige sinal, mas a cobrança online está indisponível nesta unidade.
                  Entre em contato com a unidade para consultar o agendamento.
                </p>
              ) : null}
              <AvailabilityCalendar
                month={month}
                today={today}
                maxDate={maxDate}
                value={date}
                onMonthChange={(value) => {
                  setMonth(value);
                  setDate(null);
                  setSelected(null);
                  setAccepted(false);
                  setRequestKey(crypto.randomUUID());
                }}
                onValueChange={(value) => {
                  setDate(value);
                  setSelected(null);
                  setAccepted(false);
                  setRequestKey(crypto.randomUUID());
                }}
                availability={Object.fromEntries(
                  (monthAvailability.data?.days ?? []).map((day) => [
                    day.date,
                    day.slots.length ? ('AVAILABLE' as const) : ('UNAVAILABLE' as const),
                  ]),
                )}
              />
              <Button
                variant="outline"
                className="mt-4"
                disabled={findingDay || monthAvailability.isFetching || depositUnavailable}
                onClick={() => void nextAvailableDay()}
              >
                {findingDay ? 'Buscando horários…' : 'Ver próximo dia com horários'}
              </Button>
              {!depositUnavailable ? (
                <h3 className="mb-3 mt-6 font-semibold">
                  {date
                    ? `Horários para ${date.split('-').reverse().join('/')}`
                    : 'Selecione um dia para consultar os horários'}
                </h3>
              ) : null}
              {availability.isFetching ? (
                <p role="status" className="text-sm text-content-muted">
                  Consultando disponibilidade…
                </p>
              ) : availability.isError ? (
                <p role="alert" className="text-sm text-danger">
                  Não foi possível consultar este dia. Escolha uma data nos próximos 90 dias.
                </p>
              ) : availability.data ? (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {availability.data.slots.map((item) => (
                    <button
                      key={slotKey(item)}
                      type="button"
                      aria-pressed={selected === slotKey(item)}
                      onClick={() => {
                        setSelected(slotKey(item));
                        setAccepted(false);
                        setRequestKey(crypto.randomUUID());
                      }}
                      className={`portal-slot rounded-xl border px-3 py-3 text-sm font-semibold ${selected === slotKey(item) ? 'border-brand-500 bg-brand-500/15 text-brand-600' : 'border-surface-border hover:border-brand-500/50'}`}
                    >
                      {item.startTime}–{item.endTime}
                      {pricesVary && item.priceCents !== undefined ? (
                        <span className="block text-xs font-normal">
                          {new Intl.NumberFormat('pt-BR', {
                            style: 'currency',
                            currency: service.currency,
                          }).format(item.priceCents / 100)}
                        </span>
                      ) : null}
                    </button>
                  ))}
                  {availability.data.slots.length === 0 && !depositUnavailable ? (
                    <p className="col-span-full text-sm text-content-muted">
                      Sem horários neste dia. Selecione um dia com ponto dourado no calendário.
                    </p>
                  ) : null}
                </div>
              ) : null}
            </Card>
          ) : null}
        </div>
        <aside className="min-w-0 lg:sticky lg:top-6">
          <Card>
            <CardHeader title="Seu atendimento" icon={<Clock3 className="size-4" />} />
            <p className="text-xl font-semibold">{service?.displayName ?? 'Escolha um serviço'}</p>
            <p className="mt-2 text-sm text-content-muted">
              {unit?.displayName ?? 'Escolha uma unidade'}
              {shown
                ? ` · ${
                    !slot &&
                    range?.minDurationMinutes !== undefined &&
                    range.minDurationMinutes !== range.maxDurationMinutes
                      ? `${range.minDurationMinutes} a ${range.maxDurationMinutes}`
                      : shown.durationMinutes
                  } minutos`
                : ''}
            </p>
            {prepaidId ? (
              <p className="my-6 text-lg font-semibold text-brand-600">
                Sessão do pacote · sem novo pagamento
              </p>
            ) : service ? (
              <p className="my-6 text-3xl font-semibold text-brand-600">
                {!slot &&
                range?.minPriceCents !== undefined &&
                range.minPriceCents !== range.maxPriceCents
                  ? 'a partir de '
                  : ''}
                {new Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: service.currency,
                }).format(
                  (!slot && range?.minPriceCents !== undefined
                    ? range.minPriceCents
                    : shown!.priceCents) / 100,
                )}
              </p>
            ) : null}
            {slot && !prepaidId ? (
              <div className="mb-5 space-y-2 text-sm">
                <p>
                  Adiantamento:{' '}
                  <strong>
                    {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: service!.currency,
                    }).format(slot.deposit.amountCents / 100)}
                  </strong>
                </p>
                {slot.deposit.dueAt ? (
                  <p>
                    Prazo do adiantamento: {formatDateTime(slot.deposit.dueAt, unit!.timeZone)}.
                  </p>
                ) : null}
                <p>
                  Restante:{' '}
                  {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: service!.currency,
                  }).format((shown!.priceCents - slot.deposit.amountCents) / 100)}
                </p>
                <p>Quando houver adiantamento, o pagamento será por Pix ou cartão à vista.</p>
              </div>
            ) : null}
            <p aria-live="polite" className="my-5 text-sm font-semibold">
              {slot
                ? `${date!.split('-').reverse().join('/')} · ${slot.startTime}–${slot.endTime}`
                : 'Nenhum horário selecionado.'}
            </p>
            <div className="rounded-xl border border-brand-500/20 bg-brand-500/5 p-4 text-sm leading-6">
              {slot
                ? 'Este horário está disponível para solicitação.'
                : 'Escolha um dia e um horário para solicitar.'}{' '}
              A reserva depende da confirmação da unidade. Pagar não confirma o horário.
            </div>
            {policy.data ? (
              <div className="mt-5 space-y-4">
                {prepaidId || monthAvailability.data?.cancellationSummary ? (
                  <p className="text-sm leading-6 text-content-muted">
                    {prepaidId
                      ? 'Esta solicitação usa uma sessão do pacote comprado. Valem as regras de falta e cancelamento preservadas na compra. A sessão será reservada quando a unidade confirmar o pedido.'
                      : monthAvailability.data?.cancellationSummary}
                  </p>
                ) : null}
                {!person ? (
                  <div className="space-y-3 border-t border-surface-border pt-4">
                    <p className="text-sm font-semibold">
                      Para pedir este horário, entre ou cadastre-se.
                    </p>
                    <p className="text-sm text-content-muted">
                      Sua escolha fica guardada e você volta para cá. Entrar ou criar a conta não
                      reserva o horário.
                    </p>
                    <div onClickCapture={keepChoice}>
                      <SignInButtons next={`/c/${company.key}`} />
                    </div>
                  </div>
                ) : !canRequest ? (
                  <section
                    aria-label="Troque para cliente para solicitar"
                    className="space-y-3 rounded-xl border border-warning/30 bg-warning/10 p-4"
                  >
                    <div className="flex items-center gap-2 font-semibold">
                      <ShieldCheck className="size-5 shrink-0 text-warning" aria-hidden="true" />
                      Você está administrando uma empresa
                    </div>
                    <p className="text-sm leading-6">
                      Para solicitar um horário para você, troque para o contexto de cliente. Isso
                      encerra sua atuação administrativa. Sua escolha de serviço, dia e horário será
                      mantida; a troca não envia o pedido.
                    </p>
                    <Button disabled={switchingToCustomer} onClick={() => void switchToCustomer()}>
                      {switchingToCustomer
                        ? 'Trocando para cliente…'
                        : 'Trocar para cliente e continuar'}
                    </Button>
                    {contextError ? (
                      <p role="alert" className="text-sm text-danger">
                        {contextError}
                      </p>
                    ) : null}
                  </section>
                ) : (
                  <>
                    {person.context?.kind === 'TENANT_MEMBER' ? (
                      <p className="rounded-xl border border-surface-border bg-surface-elevated p-3 text-sm leading-6">
                        Este pedido sai em seu nome, como <strong>cliente</strong>, e não pela área
                        administrativa de{' '}
                        {person.currentTenant?.displayName ?? 'onde você trabalha'}.
                      </p>
                    ) : null}
                    {linked.data === false ? (
                      <p className="rounded-xl border border-brand-500/20 bg-brand-500/5 p-3 text-sm leading-6">
                        <strong>Seu primeiro pedido em {company.displayName}.</strong> Ao pedir, a
                        empresa recebe seu nome, e-mail
                        {account.data?.account.phone ? ' e celular' : ''} para atender este pedido.
                        Novidades e promoções dela ficam desligadas; você pode ligar em Meus dados e
                        mensagens.
                      </p>
                    ) : null}
                    {account.data?.account.legalAcceptanceRequired ? (
                      <div className="space-y-3 rounded-xl border border-warning/40 bg-warning/10 p-3 text-sm leading-6">
                        <p>
                          Os{' '}
                          <Link to="/terms" className="font-semibold underline underline-offset-2">
                            Termos de Uso
                          </Link>{' '}
                          e a{' '}
                          <Link
                            to="/privacy"
                            className="font-semibold underline underline-offset-2"
                          >
                            Política de Privacidade
                          </Link>{' '}
                          mudaram. Leia e aceite antes do próximo pedido.
                        </p>
                        <Button
                          variant="outline"
                          loading={acceptingLegal}
                          onClick={() => void acceptLegal()}
                        >
                          Li e aceito os documentos atualizados
                        </Button>
                      </div>
                    ) : null}
                    <Switch checked={accepted} onChange={setAccepted}>
                      Li e aceito a política deste pedido.
                    </Switch>
                    <Button
                      disabled={
                        sending ||
                        !accepted ||
                        !slot ||
                        Boolean(prepaidId && (!selectedPackage || packages.isFetching)) ||
                        account.data?.account.legalAcceptanceRequired === true
                      }
                      onClick={() => void requestSlot()}
                    >
                      {sending ? 'Solicitando…' : 'Solicitar horário'}
                    </Button>
                  </>
                )}
              </div>
            ) : (
              <p className="mt-5 text-sm text-content-muted">
                A política precisa ser carregada antes da solicitação.
              </p>
            )}
            {requestError ? (
              <p role="alert" className="mt-4 text-sm text-danger">
                {requestError}
              </p>
            ) : null}
          </Card>
        </aside>
      </div>
    </section>
  );
}
function PortalHelp() {
  return (
    <section id="portal-help" className="mt-16 border-t border-surface-border pt-8">
      <h2 className="text-2xl font-semibold">Antes de solicitar</h2>
      <p className="mt-4 max-w-3xl text-sm leading-7 text-content-muted">
        Escolha empresa, unidade, serviço, dia e horário. O calendário mostra vagas para os próximos
        90 dias no fuso da unidade, sem revelar compromissos de outros clientes. Solicitar ou pagar
        não confirma o horário: aguarde a confirmação da equipe. Produtos não são vendidos por aqui.
      </p>
      <p className="mt-4 max-w-3xl text-sm leading-7 text-content-muted">
        Quando houver adiantamento, o pedido informa valor e prazo; o pagamento aceita Pix ou cartão
        à vista. Antes do atendimento, você pode cancelar inclusive com menos de 24 horas e escolher
        devolução integral, sem desconto de tarifas, ou crédito nesta empresa. O uso do crédito em
        outras unidades depende da configuração da unidade de pagamento. Remarcar exige sua
        concordância para reaproveitar o adiantamento e nova confirmação da equipe.
      </p>
      <p className="mt-4 max-w-3xl text-sm leading-7 text-content-muted">
        Em Meus agendamentos, acompanhe pagamentos, cancelamento e comprovantes disponíveis.
        Protocolo é registro do pedido; não comprova devolução. O provedor pode levar tempo para
        processar o estorno, e recusas ou falta de saldo exigem regularização pela empresa. Fale com
        a unidade pelos contatos publicados se precisar de ajuda. A política comercial preserva os
        direitos previstos em lei.
      </p>
    </section>
  );
}

/// Horários de mesmo início podem ter fim e preço diferentes, conforme o profissional (ADR 0074).
function slotKey(slot: { startsAt: string; endsAt: string; priceCents?: number | undefined }) {
  return `${slot.startsAt}|${slot.endsAt}|${slot.priceCents ?? ''}`;
}
